import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Baby, GraduationCap, Briefcase, Sparkles, Eye, Activity, AlertCircle } from "lucide-react";

/**
 * Página "Señales" — escaparate empático orientado a que padres y adultos
 * se reconozcan en situaciones cotidianas y descubran que el origen puede ser visual.
 *
 * Material clínico inspirado y validado por:
 *   Pilar Vergara — "Tanta inteligencia, tan poco rendimiento. ¿Podría ser
 *   la visión la clave para desbloquear su aprendizaje?" (Aurum Volatile)
 */

const GROUPS = [
  {
    id: "ninos-pequenos",
    icon: Baby,
    title: "En tu hijo/a pequeño",
    age: "2 – 6 años",
    lead: "A esta edad ellos no saben decir 'veo mal' — piensan que todos ven como ellos. Por eso lo primero que llama la atención son los gestos.",
    signals: [
      "Se acerca mucho al papel para dibujar o ver un cuento",
      "Entrecierra o guiña los ojos para ver de lejos",
      "Se frota los ojos con frecuencia",
      "Le sale un ojo desviado, sobre todo cuando está cansado",
      "Cierra o se tapa un ojo para mirar",
      "Inclina o gira la cabeza al fijar la vista",
      "Le cuesta calcular distancias — se choca con los muebles, se le caen las cosas",
      "Torpe con la pelota o con la motricidad fina (recortar, colorear)",
      "Evita las tareas de cerca: pintar, construcciones, puzles",
      "Le molesta la luz más de lo normal",
    ],
  },
  {
    id: "escolares",
    icon: GraduationCap,
    title: "En tu hijo/a en edad escolar",
    age: "6 – 16 años",
    lead: "Cuando el cole se complica, lo primero que se piensa es 'falta de atención', 'pereza' o 'no se esfuerza'. Muchas veces hay un problema visual no detectado detrás. Hasta un 80% del aprendizaje entra por la vista.",
    signals: [
      "Se cansa pronto haciendo deberes o le duele la cabeza al estudiar",
      "Lee bien pero no entiende lo que ha leído (mala comprensión lectora)",
      "Se salta líneas o palabras pequeñas al leer",
      "Usa el dedo o un marcador para no perderse",
      "Mueve mucho la cabeza al leer en vez de mover los ojos",
      "Se distrae con facilidad, periodos cortos de atención",
      "Confunde letras o palabras parecidas (b/d, ojo/oso…)",
      "Invierte letras o números al escribir",
      "Le cuesta copiar bien de la pizarra",
      "Escritura descuidada, no se mantiene sobre la línea",
      "Bajo rendimiento académico sin explicación clara",
      "Le dicen que tiene déficit de atención… pero algo no encaja",
      "Le cuesta cuando hay que cambiar la mirada de lejos (pizarra) a cerca (cuaderno)",
      "Visión doble o las letras 'se mueven' al leer un rato",
      "No le gusta leer, evita las tareas de cerca",
    ],
  },
  {
    id: "ambliopia-estrabismo",
    icon: Eye,
    title: "Ojo vago (ambliopía) y estrabismo",
    age: "Cualquier edad",
    lead: "Son dos de las condiciones visuales más comunes y peor entendidas. El \"ojo no es vago\": lo que ocurre es que el cerebro no está coordinando bien los dos ojos. Y se puede tratar a cualquier edad.",
    signals: [
      "Te han dicho que tu hijo/a tiene 'ojo vago' o ambliopía",
      "Le tuerce un ojo de forma constante o de vez en cuando (sobre todo cuando se cansa o está enfermo)",
      "Cierra un ojo para leer, al sol o cuando se concentra",
      "Dificultad para calcular distancias o ver en 3D (juegos de pelota, escaleras…)",
      "Antecedentes familiares de ojo vago o estrabismo",
      "Le pusieron parche durante un tiempo y no terminó de funcionar",
      "Tiene la corrección óptica pero sigue sin alcanzar el 100% de visión en un ojo",
      "Eres adulto y nunca se trató tu ambliopía — y crees que 'ya es tarde'",
    ],
    extra: {
      title: "Mitos vs. realidad",
      items: [
        { mito: "Después de los 7-8 años ya no se puede recuperar la visión de un ojo ambliope.", verdad: "Falso. Gracias a la plasticidad cerebral, el tratamiento es posible a cualquier edad, incluso en adultos." },
        { mito: "El único tratamiento es el parche.", verdad: "Falso. El parche aborda el síntoma, no la causa. En muchos casos no se necesita: la terapia visual enseña a los dos ojos a trabajar juntos." },
        { mito: "Si lleva muchas horas el parche, mejor.", verdad: "Falso. Llevarlo entre 4 y 6 horas es igual de eficaz que llevarlo más tiempo." },
        { mito: "El estrabismo se opera y ya está.", verdad: "La cirugía corta los músculos, pero el cerebro sigue sin saber coordinar los ojos. Por eso muchos pacientes se siguen torciendo. La terapia visual trabaja la causa." },
      ],
    },
  },
  {
    id: "adultos",
    icon: Briefcase,
    title: "En ti, adulto",
    age: "+18 años",
    lead: "No solo es cosa de niños. La carga visual de la vida adulta — pantallas, lectura, conducción — pasa factura. Y los síntomas se confunden con 'cansancio' o 'estrés'.",
    signals: [
      "Te cansas con pantallas mucho antes de terminar el día",
      "Dolor de cabeza al final de la jornada o al leer un rato",
      "Visión borrosa al cambiar de lejos a cerca (y viceversa)",
      "Conducir de noche se ha vuelto más difícil",
      "Notas que tu lectura es más lenta o se 'mueve' el texto",
      "Picor o sequedad ocular al usar el ordenador",
      "Vista borrosa o doble cuando estás cansado/a",
      "Te cuesta concentrarte aunque la tarea sea sencilla",
      "Te han diagnosticado vista cansada/présbicia y aún así no te sientes cómodo/a",
    ],
  },
  {
    id: "sdp",
    icon: Activity,
    title: "Signos posturales y de equilibrio (SDP)",
    age: "Adolescentes y adultos",
    lead: "La visión dirige el equilibrio y la postura. Cuando esa conexión falla, el cuerpo lo compensa con dolor, mareo o sensación de inestabilidad — sin que ninguna prueba estructural encuentre la causa.",
    signals: [
      "Dolor crónico de cabeza, cuello, hombros o espalda sin causa estructural clara",
      "Mareos, vértigo o sensación de inestabilidad",
      "Mala postura al trabajar o escribir, te 'tumbas' sobre la mesa",
      "Mueves la cabeza en vez de los ojos al leer",
      "Tu fisioterapeuta u osteópata sospecha que hay un componente visual",
      "Te canses como te canses, los síntomas vuelven",
      "Sensación general de fatiga visual desproporcionada al esfuerzo",
    ],
  },
];

const SignalsPage = () => {
  return (
    <main data-testid="signals-page">
      {/* HERO */}
      <section className="py-20 px-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-4xl mx-auto fade-up">
          <span className="section-eyebrow">¿Te suena alguna de estas situaciones?</span>
          <h1 className="section-title mt-4">
            Señales que tu <span className="handwritten">visión</span>
            <br />puede estar pidiendo ayuda
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 max-w-2xl text-lg leading-relaxed">
            Muchos problemas que se viven como <em>"falta de atención"</em>, <em>"pereza"</em>, <em>"dolor de cabeza por estrés"</em> o <em>"vista cansada"</em> esconden detrás un problema visual no detectado. La buena noticia: <strong>tienen solución, y a cualquier edad.</strong>
          </p>
          <div className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans-body text-xs" style={{ background: "var(--sage-100)", color: "var(--sage-700)" }}>
            <BookOpen size={14} /> Material basado en la guía clínica de Pilar Vergara, optometrista
          </div>
        </div>
      </section>

      {/* QUICK NAV */}
      <section className="px-6 -mt-8">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-2 justify-center">
          {GROUPS.map((g) => (
            <a key={g.id} href={`#${g.id}`} className="px-4 py-2 rounded-full font-sans-body text-sm transition-colors" style={{ background: "var(--cream)", color: "var(--sage-600)", border: "1px solid var(--sage-100)" }} data-testid={`signals-quicklink-${g.id}`}>
              {g.title}
            </a>
          ))}
        </div>
      </section>

      {/* GROUPS */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto space-y-20">
          {GROUPS.map((g, idx) => (
            <article key={g.id} id={g.id} className="scroll-mt-24" data-testid={`signals-group-${g.id}`}>
              <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-4">
                  <div className="sticky top-28">
                    <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--sage-500)" }}>
                      <g.icon size={28} style={{ color: "var(--cream)" }} />
                    </div>
                    <div className="section-eyebrow">{String(idx + 1).padStart(2, "0")}</div>
                    <h2 className="text-3xl font-medium mt-2" style={{ color: "var(--ink)" }}>{g.title}</h2>
                    <p className="font-display text-xl mt-2" style={{ color: "var(--sage-500)" }}>{g.age}</p>
                  </div>
                </div>

                <div className="md:col-span-8">
                  <p className="font-sans-body text-[color:var(--ink-soft)] leading-relaxed text-lg">{g.lead}</p>

                  <ul className="mt-7 grid sm:grid-cols-2 gap-3 font-sans-body">
                    {g.signals.map((s, i) => (
                      <li key={i} className="flex gap-3 items-start p-3 rounded-xl" style={{ background: "var(--bg-soft)", border: "1px solid var(--sage-100)" }}>
                        <span className="h-1.5 w-1.5 rounded-full mt-2.5 shrink-0" style={{ background: "var(--sage-500)" }} />
                        <span className="text-sm text-[color:var(--ink)]">{s}</span>
                      </li>
                    ))}
                  </ul>

                  {g.extra && (
                    <div className="mt-8 p-6 rounded-2xl" style={{ background: "var(--sage-50)", border: "1px solid var(--sage-100)" }}>
                      <h3 className="section-eyebrow flex items-center gap-2"><AlertCircle size={14} /> {g.extra.title}</h3>
                      <ul className="mt-4 space-y-4">
                        {g.extra.items.map((it, i) => (
                          <li key={i} className="font-sans-body">
                            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>
                              <span className="font-semibold" style={{ color: "var(--terracotta, #c98466)" }}>Mito.</span> {it.mito}
                            </div>
                            <div className="text-sm mt-1" style={{ color: "var(--ink)" }}>
                              <span className="font-semibold" style={{ color: "var(--sage-600)" }}>Realidad.</span> {it.verdad}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Mini CTA dentro del grupo */}
                  <div className="mt-8 p-5 rounded-2xl flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--cream)", border: "1px dashed var(--sage-200)" }}>
                    <p className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>
                      <Sparkles size={14} className="inline mr-2" style={{ color: "var(--sage-500)" }} />
                      ¿Te has reconocido en varias señales? <strong>Tiene solución, y a cualquier edad.</strong>
                    </p>
                    <Link to="/servicios#autoevaluacion" className="btn-primary" data-testid={`signals-cta-${g.id}`}>
                      Haz tu autoevaluación <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-6" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="section-title" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
            La <span className="handwritten">visión se aprende</span> — y se puede reaprender
          </h2>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 max-w-2xl mx-auto leading-relaxed">
            Gracias a la <strong>plasticidad cerebral</strong>, el sistema visual puede reorganizarse a cualquier edad. Cuanto antes se actúe, mejor — pero nunca es tarde. Si te has reconocido en algún bloque, lo siguiente es sencillo: hablamos.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/servicios#autoevaluacion" className="btn-primary" data-testid="signals-final-cta-autoeval">Haz tu autoevaluación <ArrowRight size={16} /></Link>
            <Link to="/#contacto" className="btn-ghost" data-testid="signals-final-cta-contact">Pide cita <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SignalsPage;
